// @flow

import type { Videos } from 'app/packages/pinterest-flow-types/video';
import type { VideosAutoplaying } from 'app/common/redux/reducers/ui/app';
import { type Pin as ExperimentalPin } from 'app/common/react/components/PinRep/types';
import type {
  Attribution,
  CloseupPin,
  Embed,
  Pin,
  SummaryPin,
} from 'app/packages/pinterest-flow-types/pin';

// Returns true for organic videos only (as in, has a videos object with a video_list)
export const isVideoPin = (videos: ?{ video_list: Videos }) => !!videos && videos.video_list;

export const isEmbeddedVideo = (embed: ?Embed, attribution: ?Attribution) => {
  const embedded = embed || (attribution && attribution.embed);
  return embedded && embedded.src && !embedded.type;
};

export const isProductPin = (pin: Pin) =>
  (pin.rich_summary && pin.rich_summary.products && pin.rich_summary.products.length > 0) ||
  (pin.rich_metadata && pin.rich_metadata.products && pin.rich_metadata.products.length > 0);

export const isPromotedPin = (pin: Pin | ExperimentalPin) =>
  !!pin.promoter && !pin.is_downstream_promotion;

export const isGifPin = (pin: SummaryPin | CloseupPin) => {
  const embedded = pin.embed || (pin.attribution && pin.attribution.embed) || null;
  return embedded && embedded.type === 'gif';
};

export const isStatusPin = (pin: { video_status?: number }) =>
  !!pin.video_status && pin.video_status !== PCONST.thrift.VideoPinStatus.SUCCESS;

export const buildPinRepLink = (pinId: string, isEligibleForAnalyticsCloseup: boolean): string => {
  if (isEligibleForAnalyticsCloseup) {
    return `/pin/${pinId}/analytics`;
  }
  return `/pin/${pinId}/`;
};

export const isVideoPlaying = (pinId: string, videosAutoplaying: VideosAutoplaying) => {
  const { organicVideosAutoplaying, promotedVideosAutoplaying } = videosAutoplaying;
  const combinedMap = {
    ...organicVideosAutoplaying,
    ...promotedVideosAutoplaying,
  };

  return !!combinedMap[pinId] && !combinedMap[pinId].paused;
};

export const areAllOtherVideosPaused = (pinId: string, videosAutoplaying: VideosAutoplaying) => {
  const { organicVideosAutoplaying, promotedVideosAutoplaying } = videosAutoplaying;
  const combinedMap = {
    ...organicVideosAutoplaying,
    ...promotedVideosAutoplaying,
  };

  for (const key in combinedMap) {
    const { paused } = combinedMap[key];
    // check all other videos' paused state except itself
    if (key !== pinId && !paused) {
      return false;
    }
  }

  return true;
};

export const getCurrentVideoTime = (pinId: string, videosAutoplaying: VideosAutoplaying) => {
  const { organicVideosAutoplaying, promotedVideosAutoplaying } = videosAutoplaying;
  const combinedMap = {
    ...organicVideosAutoplaying,
    ...promotedVideosAutoplaying,
  };

  return combinedMap[pinId] && combinedMap[pinId].currentTime;
};

export default {
  areAllOtherVideosPaused,
  buildPinRepLink,
  getCurrentVideoTime,
  isEmbeddedVideo,
  isGifPin,
  isProductPin,
  isPromotedPin,
  isStatusPin,
  isVideoPin,
  isVideoPlaying,
};
